import { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import i18n from "@/shared/config/i18n";
import { captureAttributionFromUrl } from "@/shared/lib/analytics";
import { isAppWindow } from "@/shared/lib/appWindow";
import { captureReferralFromUrl } from "@/shared/lib/referral";
import { syncReferralArrival } from "@/features/affiliate/model/referralArrival";
import AppRouter from "@/app/providers/router/ui/AppRouter";
import { WebHandoffGate } from "@/app/providers/router/ui/WebHandoffGate";
import { ensureR2SessionToken } from "@/features/bucket/api";
import { ImpersonationBanner } from "@/features/admin/impersonation/ui/ImpersonationBanner";
import { ActivityLogRoot } from "@/app/providers/ActivityLog/ui/ActivityLogRoot";
import { AnalyticsRoot } from "@/app/providers/Analytics/ui/AnalyticsRoot";
import { PointsInfoRoot } from "@/features/pointsPulse/ui/PointsInfoRoot";
import { ErrorReportRoot } from "@/features/errorReport/ui/ErrorReportRoot";
import { routePattern } from "@/app/providers/ActivityLog/lib/routePattern";
import { AchievementCardRoot } from "@/features/achievements/ui/AchievementCardRoot";
import { TokenPurchaseRoot } from "@/features/tokenPurchase/ui/TokenPurchaseRoot";
import { PlanPurchaseRoot } from "@/features/supPlan/ui/PlanPurchaseRoot";
import { FoodPackPurchaseRoot } from "@/features/recipes/foodPhoto/ui/FoodPackPurchaseRoot";
import { ArabicUiShell } from "@/app/providers/ArabicUi";

export const App = () => {
    const location = useLocation();

    useEffect(() => {
        const langFromUrl = location.pathname.split("/")[1];
        const supportedLangs = ["ru", "en", "cn", "ar", "es", "de"];
        const lang = supportedLangs.includes(langFromUrl) ? langFromUrl : "en";
        i18n.changeLanguage(lang);
    }, [location.pathname]);

    // Capture campaign attribution (utm_*, and for GA4 the Google Ads click
    // ids gclid / gbraid / wbraid — shared/lib/analytics/attribution.ts) from
    // the landing URL so checkouts can tag the purchase with it. A friend's referral link
    // (`?ref=<handle>`) is caught on any page too and kept for 30 days, so
    // any later purchase — a combo instead of the linked course included —
    // credits the friend (shared/lib/referral.ts).
    useEffect(() => {
        captureAttributionFromUrl();
        captureReferralFromUrl();
    }, [location.search]);

    // Пришёл по ссылке друга и вошёл — воркеру «пришёл по ссылке» (один раз):
    // пригласившему баллы за установку приложения и начало занятий
    // (features/affiliate/model/referralArrival). На каждой смене страницы —
    // вход часто кончается переходом в кабинет; уже сообщённое не повторяется.
    useEffect(() => {
        void syncReferralArrival();
    }, [location.pathname, location.search]);

    // Reissue r2_session_token on app mount if the user looks logged
    // in (auth_token present) but the bucket worker token isn't.
    // Stops the 403 spam on watch-later / history / favorites sync
    // calls without forcing the user to re-login. Best-effort no-op
    // when there's nothing to reissue. (Recovering the Google name and
    // photo lives in UserProvider, which owns the user state.)
    useEffect(() => {
        void ensureR2SessionToken();
    }, []);

    // Окно приложения (?source=app в первом адресе) запоминается на всю
    // вкладку — до того, как переходы по сайту уберут метку из адреса.
    useEffect(() => {
        isAppWindow();
    }, []);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.slice(1));

            if (element) {
                requestAnimationFrame(() => {
                    element.scrollIntoView({ block: "start" });
                });
                return;
            }
        }

        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, [location.pathname, location.hash]);

    // Арабский интерфейс (shared/config/arabicUi) — только /ar и только когда он
    // включён: рубильником AR_UI_ENABLED или предпросмотром админа на этом
    // устройстве (?ar_preview=1). Иначе ArabicUiShell не меняет ничего.
    return (
        <ArabicUiShell pathname={location.pathname} search={location.search}>
            <Suspense fallback="">
                <ImpersonationBanner />
                {/* GA4 через GTM; до журнала — мост в GA4 ставится раньше его старта. */}
                <AnalyticsRoot />
                <ActivityLogRoot />
                {/* Нажатие на любые баллы — окно «за что эти баллы». */}
                <PointsInfoRoot />
                {/* «Нашли ошибку?» внизу слева, Ctrl+Enter с выделением, лист для рецептов и «Что в блюде». */}
                <ErrorReportRoot routeOf={routePattern} />
                {/* Нажатие на любую картинку шкалы или достижения — карточка «за что». */}
                <AchievementCardRoot />
                {/* Вернулись со Stripe после покупки токенов — на любой странице. */}
                <TokenPurchaseRoot />
                {/* …и после оплаты пакета «поиск + вопросы специалисту» (?plan_paid=1). */}
                <PlanPurchaseRoot />
                {/* …и пакета «+60 распознаваний» в «Что в блюде» (?food_paid=1). */}
                <FoodPackPurchaseRoot />
                {/* A login code from the app (?code=&source=app) is redeemed
                    before any page renders, public or account alike. */}
                <WebHandoffGate>
                    <AppRouter />
                </WebHandoffGate>

            </Suspense>
        </ArabicUiShell>
    );
};
